export const MODEL_TIERS = [
  { id: "max", label: "Max" },
  { id: "medium", label: "Medium" },
  { id: "low", label: "Low" },
];

export const DEFAULT_MODEL_TIER = "medium";

const normalizeKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const providerFamily = (providerName) => {
  const key = normalizeKey(providerName);
  if (!key) return "";
  if (key.includes("openai") || key === "gpt") return "openai";
  if (key.includes("claude") || key.includes("anthropic")) return "claude";
  if (key.includes("gemini") || key.includes("google")) return "gemini";
  return key;
};

const TIER_MATCHERS = {
  openai: {
    max: (version) => /\bsol\b/.test(version),
    medium: (version) => /\bterra\b/.test(version),
    low: (version) => /\bluna\b/.test(version),
  },
  claude: {
    max: (version) => /\bopus\b/.test(version),
    medium: (version) => /\bsonnet\b/.test(version),
    low: (version) => /\bhaiku\b/.test(version),
  },
  gemini: {
    max: (version) => /\bpro\b/.test(version) && !/\bflash\b/.test(version),
    medium: (version) =>
      /\bflash\b/.test(version) && /3\.?\s*6/.test(version),
    low: (version) =>
      /\bflash\b/.test(version) && /2\.?\s*5/.test(version),
  },
};

const matchModelForTier = (models, providerName, tierId) => {
  const family = providerFamily(providerName);
  const matcher = TIER_MATCHERS[family]?.[tierId];
  if (!matcher) return null;

  return (
    models.find((model) => matcher(normalizeKey(model.modelVersion))) ?? null
  );
};

/** When names don't match known patterns, fall back by list order. */
const fallbackModelForTier = (models, tierId) => {
  if (!models?.length) return null;
  if (models.length === 1) return models[0];

  const indexByTier = {
    max: 0,
    medium: Math.min(1, models.length - 1),
    low: models.length - 1,
  };
  return models[indexByTier[tierId] ?? 0] ?? models[0];
};

export const resolveModelForTier = (models, providerName, tierId) => {
  if (!models?.length || !tierId) return null;
  return (
    matchModelForTier(models, providerName, tierId) ||
    fallbackModelForTier(models, tierId)
  );
};

export const getTierLabel = (tierId) =>
  MODEL_TIERS.find((tier) => tier.id === tierId)?.label ?? "Medium";

export const formatProviderLabel = (name) => {
  const value = String(name ?? "").trim();
  if (!value) return "Provider";
  if (value.toLowerCase() === "openai") return "OpenAI";
  return value.charAt(0).toUpperCase() + value.slice(1);
};
